import React, {FC, useEffect, useMemo} from 'react';

//MUI
import Stack from '@mui/material/Stack';

//CLAS
import Logos from '@/components/Logos';
import FilterPanel from '@/components/FilterPanel/FilterPanel';
import InfoPanel from '@/components/InfoPanel/InfoPanel';
import NavigationMenu from '@/components/NavigationMenu/NavigationMenu';

//UTILS
import {Precinct} from '@/domain/entities/Precinct';
import {Profile} from '@/domain/entities/Profile';
import {FilterGroup, Manifest} from '@/domain/services/Clas';
import {ROLE, SIDEPANEL_CONTENT_ID} from '@/config';
import {Parcel} from '@/domain/entities/Parcel';

const stackSx = {
  height: '100%',
  overflow: 'hidden'
};

export type SidePanelContentProps = {
  owner?: Profile,
  mapDetail: Array<string>,
  mapStyleId: string,
  PNOASelectedYear: number,
  overlayIds: Array<string>,
  manifest?: Manifest,
  selectedPrecinct?: Precinct,
  selectedParcel?: Parcel,
  profile?: Profile,
  sidePanelContentId?: string,
  users?: Array<Profile>,
  year?: number,
  filterGroup: FilterGroup,
  onYearChange: (year: number) => void
  onCropChange: (value: Partial<Precinct>) => void
  onUseChange: (value: Partial<Precinct>) => void
  onFilterChange: (filterGroup: FilterGroup) => void,
  onMapDetailChange: (detail: Array<string>) => void
  onPNOAYearChange: (value: number) => void,
  onStyleChange: (mapStyleId: string) => void,
  onOverlayChange: (overlay: Array<string>) => void,
}

const SidePanelContent: FC<SidePanelContentProps> = ({
  owner,
  mapDetail,
  mapStyleId,
  PNOASelectedYear,
  overlayIds,
  onMapDetailChange,
  onStyleChange,
  onPNOAYearChange,
  manifest,
  selectedPrecinct,
  selectedParcel,
  profile,
  sidePanelContentId,
  users,
  year,
  filterGroup,
  onYearChange,
  onFilterChange,
  onCropChange,
  onUseChange,
  onOverlayChange
}) => {

  const {crops, municipalities, uses, enpTypes, rnTypes, mabNames, availableYears} = manifest || {};
  const [isExpanded, setExpanded] = React.useState(true);
  
  const isAdmin = profile?.role === ROLE.ADMIN;

  useEffect(() => {
    if(selectedPrecinct && selectedParcel) {
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  }, [selectedPrecinct, selectedParcel]);

  const handleExpandedChange = (expanded: boolean) => {
    setExpanded(expanded);
  };

  const sidePanelContent = useMemo(() => (
    {
      [SIDEPANEL_CONTENT_ID.FILTERS]: <>
        <FilterPanel
          isAdmin={isAdmin}
          isExpanded={isExpanded}
          availableYears={availableYears}
          crops={crops}
          municipalities={municipalities}
          users={users}
          uses={uses}
          enpTypes={enpTypes}
          rnTypes={rnTypes}
          mabNames={mabNames}
          year={year}
          filterGroup={filterGroup}
          onYearChange={onYearChange}
          onFilterChange={onFilterChange}
          onExpandedChange={handleExpandedChange}
        />
        {
          selectedPrecinct && selectedParcel && profile
            ? <InfoPanel
              isAdmin={isAdmin}
              municipalities={municipalities}
              owner={owner}
              crops={crops}
              uses={uses}
              precinct={selectedPrecinct}
              parcel={selectedParcel}
              onCropChange={onCropChange}
              onUseChange={onUseChange}
            />
            : undefined
        }
      </>,
      [SIDEPANEL_CONTENT_ID.LAYERS]: <NavigationMenu
        mapDetail={mapDetail}
        mapStyleId={mapStyleId}
        PNOASelectedYear={PNOASelectedYear}
        overlayIds={overlayIds}
        onMapDetailChange={onMapDetailChange}
        onOverlayChange={onOverlayChange}
        onPNOAYearChange={onPNOAYearChange}
        onStyleChange={onStyleChange}
      />
    }
  ), [users, selectedPrecinct, selectedParcel, profile, mapDetail, mapStyleId, PNOASelectedYear, overlayIds, year, filterGroup, isAdmin, isExpanded, availableYears, crops, municipalities, uses, enpTypes, rnTypes, mabNames, owner]);

  return <>
    {
      sidePanelContentId && <Stack sx={stackSx}>
        {sidePanelContent[sidePanelContentId]}
        <Logos/>
      </Stack>
    }
  </>;
};

export default SidePanelContent;